'use client'

import { useQuery } from '@tanstack/react-query'
import { Info, TriangleAlert } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle, Button } from '@ui/web/components'
import { getDaysBetween } from '@helper/utils/date'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { BillingType } from './util'
import { useSharedTRPC } from '../../context'
import { checkoutPaths, FOREIGN_ACCESS_WARNING_DAYS, foreignSourceLabel } from './util'

interface ForeignAccessNoticeProps {
  type: BillingType
  /** Navigates inside the host app — receives an app-relative path such as `/checkout/new`. */
  onNavigate: (path: string) => void
  className?: string
}

/**
 * Premium access this organization's children hold on someone else's seats.
 *
 * Sits under the plan strips and answers a question those strips cannot: why a screen reading
 * "Starter" belongs to a child who is demonstrably opening premium books. After Sambung
 * Sekolah-Rumah one child is a member of both a school and a family while holding a single seat, and
 * that seat unlocks premium on both sides — so whichever side did not buy it has real access and an
 * empty pool list.
 *
 * DELIBERATELY NOT A POOL STRIP, and never a renewal surface. These seats belong to another
 * organization's billing cycle: they cannot be renewed, topped up or invoiced here, they are absent
 * from Tersedia/Terpakai, and the expiry reminder cron — which walks owned pools — will never write
 * about them. That is exactly why the notice has to say the date itself: without it the access
 * simply stops one morning with nothing having warned anybody. The CTA is therefore "Jadi Premium"
 * (a new plan), never "Perpanjang" — there is nothing here this organization could extend.
 *
 * A school sees the same fact collapsed to one line. Its sources are individual households, so
 * naming them would print a list of family names that grows with the roster and tells a school
 * nothing it can act on; the count is the part that matters, and it is the same reach the premium
 * locks count against.
 */
export function ForeignAccessNotice({ type, onNavigate, className }: ForeignAccessNoticeProps) {
  const trpc = useSharedTRPC()

  const { data: coverage } = useQuery(trpc.myOrganization.getForeignPremiumCoverage.queryOptions())

  // Absent for almost every organization — no coverage, still loading, or a failed read. None of the
  // three should print a placeholder: this notice explains a surprise, and a skeleton where there is
  // usually nothing at all reads as something being wrong.
  if (!coverage || coverage.length === 0) return null

  // Soonest-ending first from the query, so the head is the access that lapses next. That is the
  // date worth warning about; a later one is not yet news.
  const soonest = coverage[0]
  if (!soonest) return null

  const endsAt = new Date(soonest.endsAt)
  const { totalDays } = getDaysBetween(new Date(), endsAt)
  const isEndingSoon = totalDays <= FOREIGN_ACCESS_WARNING_DAYS

  const totalChildren = coverage.reduce((sum, row) => sum + row.childCount, 0)

  return (
    <Alert variant='neutral' className={className}>
      {/* The icon rides inside the title rather than in `Alert`'s own icon slot, which is driven by
          a `calc(var(--spacing)*5)` that does not resolve under the `srd` prefix — see the same
          workaround in `GrantSeatsNotice`. */}
      <AlertTitle className='srd:flex srd:items-center srd:gap-x-2'>
        {isEndingSoon ? (
          <TriangleAlert className='srd:size-5 srd:shrink-0' aria-hidden />
        ) : (
          <Info className='srd:size-5 srd:shrink-0' aria-hidden />
        )}
        {isEndingSoon ? 'Akses Premium akan berakhir' : 'Akses Premium dari luar'}
      </AlertTitle>

      <AlertDescription className='srd:flex srd:flex-col srd:gap-y-2'>
        {type === 'school' ? (
          <span>
            {totalChildren} murid memakai kursi Premium dari Premium mandiri orang tua. Kursi itu tidak masuk hitungan
            kursi sekolah, tetapi tetap dihitung sebagai murid Premium. Yang paling awal berakhir pada{' '}
            {formatDate(endsAt, { dateStyle: 'long' })}.
          </span>
        ) : (
          <div className='srd:flex srd:flex-col'>
            {coverage.map((row) => (
              <span key={row.organizationId ?? 'tanpa-organisasi'}>
                {row.childCount > 1 ? `${row.childCount} anak memakai a` : 'A'}kses Premium dari{' '}
                {foreignSourceLabel(row)} sampai {formatDate(new Date(row.endsAt), { dateStyle: 'long' })}
              </span>
            ))}
          </div>
        )}

        {/* Family only. A school that wants more Premium students has its own seat controls right
            above this notice; a family carried by a school seat has no plan at all, so the honest
            offer is a new one. */}
        {type === 'web' && isEndingSoon && (
          <Button
            variant='link'
            className={cn('srd:h-auto srd:self-start srd:p-0 srd:underline')}
            onClick={() => onNavigate(checkoutPaths.newPlan)}
          >
            Jadi Premium
          </Button>
        )}
      </AlertDescription>
    </Alert>
  )
}
